import MUICreateTheme from "@mui/material/styles/createTheme";
import MUIRedColor from "@mui/material/colors/red";
import MUIGreenColor from "@mui/material/colors/green";
import MUIBlueColor from "@mui/material/colors/blue";
import MUIGreyColor from "@mui/material/colors/grey";

/**
 * Defines the light Sales Pilot theme as application-owned color tokens.
 * @returns {object} Light theme definition.
 */
function defineLightTheme() {
  return {
    name: "light",
    label: "Light",
    mode: "light",
    background: "#e6eef8",
    backgroundHover: "#e8ecf2",
    paper: "#ffffff",
    textPrimary: "#212121",
    textSecondary: "#666666",
    textDisabled: "#9e9e9e",
    divider: "#e0e0e0",
    actionActive: "#757575",
    actionDisabled: "#bdbdbd",
    actionHover: "rgba(0, 0, 0, 0.04)",
    actionSelected: "rgba(0, 0, 0, 0.08)",
    primary: "#1e4670",
    secondary: "#5c9acd",
    accent: "#bad3ee",
    appBar: "#1e4670",
    appBarText: "#ffffff",
    dataGridRowOddBackground: MUIGreyColor[200],
    dataGridRowOddHoverBackground: MUIGreyColor[300],
    dataGridRowEvenBackground: MUIGreyColor[50],
    dataGridRowEvenHoverBackground: MUIGreyColor[300],
    dataGridOutOfBoundsRowOddBackground: MUIRedColor[100],
    dataGridOutOfBoundsRowOddHoverBackground: MUIRedColor[300],
    dataGridOutOfBoundsRowEvenBackground: MUIRedColor[200],
    dataGridOutOfBoundsRowEvenHoverBackground: MUIRedColor[300],
    dataGridSearchNoResultsBackground: MUIRedColor[100],
    dataGridSearchNoResultsBorder: MUIRedColor[400],
    dataGridPriorityIcon: MUIRedColor[500],
    dataGridPriorityIconOnOutOfBoundsRow: MUIRedColor[500],
    dataGridCalendarIcon: MUIRedColor[500],
    dataGridCalendarIconOnOutOfBoundsRow: MUIRedColor[500],
    dataGridInformationIcon: MUIBlueColor[500],
    dataGridInformationIconOnOutOfBoundsRow: MUIBlueColor[500],
    dataGridSuccessIcon: MUIGreenColor[700],
    dataGridSuccessIconOnOutOfBoundsRow: MUIGreenColor[700],
    dataGridPagerText: MUIGreyColor[900],
  };
}

/**
 * Defines the dark Sales Pilot theme as application-owned color tokens.
 * @returns {object} Dark theme definition.
 */
function defineDarkTheme() {
  return {
    name: "dark",
    label: "Dark",
    mode: "dark",
    background: "#10151b",
    backgroundHover: "#26313d",
    paper: "#182029",
    textPrimary: "#e8eef5",
    textSecondary: "#a7b4c2",
    textDisabled: "#768391",
    divider: "#313942",
    actionActive: "#d2dbe5",
    actionDisabled: "#687480",
    actionHover: "rgba(232, 238, 245, 0.07)",
    actionSelected: "rgba(126, 177, 222, 0.16)",
    primary: "#7eb1de",
    secondary: "#9bacc0",
    accent: "#d4c978",
    appBar: "#141b22",
    appBarText: "#e8eef5",
    dataGridRowOddBackground: "#1b242e",
    dataGridRowOddHoverBackground: "#293746",
    dataGridRowEvenBackground: "#151c24",
    dataGridRowEvenHoverBackground: "#263441",
    dataGridOutOfBoundsRowOddBackground: "#48272c",
    dataGridOutOfBoundsRowOddHoverBackground: "#5b3138",
    dataGridOutOfBoundsRowEvenBackground: "#3c2328",
    dataGridOutOfBoundsRowEvenHoverBackground: "#553038",
    dataGridSearchNoResultsBackground: "#3c2328",
    dataGridSearchNoResultsBorder: "#c96872",
    dataGridPriorityIcon: "#ef737c",
    dataGridPriorityIconOnOutOfBoundsRow: "#ffd4d7",
    dataGridCalendarIcon: "#ef9a61",
    dataGridCalendarIconOnOutOfBoundsRow: "#ffdbbf",
    dataGridInformationIcon: "#69b9eb",
    dataGridInformationIconOnOutOfBoundsRow: "#c9e9fc",
    dataGridSuccessIcon: "#70cf98",
    dataGridSuccessIconOnOutOfBoundsRow: "#c9f1d9",
    dataGridPagerText: "#c7d1dc",
  };
}

/**
 * Gets every theme available to Sales Pilot.
 * @returns {object[]} Theme definitions in display order.
 */
export function getSalesPilotThemes() {
  return [defineLightTheme(), defineDarkTheme()];
}

/**
 * Creates a Material UI theme from a named Sales Pilot theme definition.
 * @param {string} themeName - Name of a theme returned by getSalesPilotThemes.
 * @returns {object} Material UI theme object.
 */
export function createSalesPilotTheme(themeName) {
  const themes = getSalesPilotThemes();
  const definition =
    themes.find((candidate) => candidate.name === themeName) || themes[0];

  const theme = MUICreateTheme({
    palette: {
      mode: definition.mode,
      background: {
        default: definition.background,
        hover: definition.backgroundHover,
        paper: definition.paper,
      },
      text: {
        primary: definition.textPrimary,
        secondary: definition.textSecondary,
        disabled: definition.textDisabled,
      },
      divider: definition.divider,
      action: {
        active: definition.actionActive,
        disabled: definition.actionDisabled,
        hover: definition.actionHover,
        selected: definition.actionSelected,
      },
      primary: {
        main: definition.primary,
      },
      secondary: {
        main: definition.secondary,
      },
      accent: {
        main: definition.accent,
      },
      dataGrid: {
        row: {
          oddBackground: definition.dataGridRowOddBackground,
          oddHoverBackground: definition.dataGridRowOddHoverBackground,
          evenBackground: definition.dataGridRowEvenBackground,
          evenHoverBackground: definition.dataGridRowEvenHoverBackground,
        },
        outOfBoundsRow: {
          oddBackground: definition.dataGridOutOfBoundsRowOddBackground,
          oddHoverBackground:
            definition.dataGridOutOfBoundsRowOddHoverBackground,
          evenBackground: definition.dataGridOutOfBoundsRowEvenBackground,
          evenHoverBackground:
            definition.dataGridOutOfBoundsRowEvenHoverBackground,
        },
        searchNoResults: {
          background: definition.dataGridSearchNoResultsBackground,
          border: definition.dataGridSearchNoResultsBorder,
        },
        icon: {
          priority: {
            standard: definition.dataGridPriorityIcon,
            onOutOfBoundsRow: definition.dataGridPriorityIconOnOutOfBoundsRow,
          },
          calendar: {
            standard: definition.dataGridCalendarIcon,
            onOutOfBoundsRow: definition.dataGridCalendarIconOnOutOfBoundsRow,
          },
          information: {
            standard: definition.dataGridInformationIcon,
            onOutOfBoundsRow:
              definition.dataGridInformationIconOnOutOfBoundsRow,
          },
          success: {
            standard: definition.dataGridSuccessIcon,
            onOutOfBoundsRow: definition.dataGridSuccessIconOnOutOfBoundsRow,
          },
        },
        pager: {
          text: definition.dataGridPagerText,
        },
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: definition.appBar,
            backgroundImage: "none",
            color: definition.appBarText,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  });

  theme.name = definition.name;
  theme.label = definition.label;

  return theme;
}
